import { Component, OnDestroy, OnInit } from '@angular/core'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { Subscription, combineLatest } from 'rxjs'
import { finalize, first, switchMap } from 'rxjs/operators'
import { AccountSubjectDto, AccountSubjectGroupDto, AuthenticatedPrincipalDto, RoleDto } from 'src/app/services/dtos'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from '../../services/account.service'
import { UiService } from 'src/app/services/ui.service'
import { ApiService } from 'src/app/services/api.service'
import { BaseError } from '../../../../utils/errors'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { computeSsoRolesAndGroups } from 'src/app/shared/sso-utils'

interface RoleDisplay {
  name: string
  isFromGroup: boolean
  isSsoRole: boolean
  groupNames: string[]
}

interface UserProfileData {
  email: string
  name: string
  accountSubjectId: string
  roles: RoleDisplay[]
  groups: string[]
  ssoRoles: { name: string; id: string }[]
  ssoGroups: { name: string; id: string }[]
}

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  standalone: false,
})
export class ProfileComponent implements OnInit, OnDestroy {
  nameModel = this.formBuilder.group({
    name: ['', [Validators.required]],
  })

  userProfileData: UserProfileData = null
  isReadOnly = false // True when user has access but isn't a member of the account
  private updateUserSubscription: Subscription = null
  private nameUpdated = false
  private userDataSubscription: Subscription = null

  constructor(
    private formBuilder: UntypedFormBuilder,
    private uiService: UiService,
    private apiService: ApiService,
    public accountService: AccountService,
    private accessLevelService: AccessLevelService
  ) {}

  ngOnInit() {
    this.loadUserProfile()
  }

  ngOnDestroy() {
    if (subscriptionActive(this.updateUserSubscription)) {
      this.updateUserSubscription.unsubscribe()
      this.updateUserSubscription = null
    }

    if (subscriptionActive(this.userDataSubscription)) {
      this.userDataSubscription.unsubscribe()
      this.userDataSubscription = null
    }
  }

  private loadUserProfile() {
    // Use the cached getUserPermissions which internally calls getAuthenticatedPrincipal
    this.userDataSubscription = this.accessLevelService
      .getUserPermissions()
      .pipe(
        first(),
        switchMap(() => {
          // Get authenticated principal from the API service
          return this.apiService.getAuthenticatedPrincipal().pipe(
            first(),
            switchMap((principal: AuthenticatedPrincipalDto) => {
              // Get all account subjects, roles, and groups
              return combineLatest([
                this.apiService.listAccountSubjects(this.accountService.accountId).pipe(first()),
                this.apiService.listRoles(this.accountService.accountId).pipe(first()),
                this.apiService.listAccountSubjectGroups(this.accountService.accountId).pipe(first()),
              ]).pipe(
                switchMap(([accountSubjects, roles, groups]) => {
                  // Find the account subject that matches the authenticated principal
                  // Try multiple matching strategies for native and SSO users
                  const currentAccountSubject = accountSubjects.find(
                    (subject) =>
                      subject.subjectId === principal.subjectId ||
                      principal.effectiveAccountSubjectIds?.includes(subject.accountSubjectId)
                  )

                  if (!currentAccountSubject) {
                    // User has access to this account but isn't a member (e.g., admin viewing another account)
                    // Show read-only profile with principal info
                    this.isReadOnly = true
                    this.userProfileData = {
                      email: principal.subjectId || 'N/A',
                      name: '',
                      accountSubjectId: '',
                      roles: [],
                      groups: [],
                      ssoRoles: [],
                      ssoGroups: [],
                    }
                    this.nameModel.setValue({ name: '' })
                    return []
                  }

                  this.buildUserProfileData(principal, currentAccountSubject, roles, groups)
                  return []
                })
              )
            })
          )
        })
      )
      .subscribe({
        error: (error) => {
          console.error('Error loading profile:', error)
          this.uiService.announceError(
            new BaseError('Profile Load Error', 'Unable to load your profile information. Please try again.')
          )
        },
      })
  }

  private buildUserProfileData(
    principal: AuthenticatedPrincipalDto,
    accountSubject: AccountSubjectDto,
    allRoles: RoleDto[],
    allGroups: AccountSubjectGroupDto[]
  ) {
    // Map role IDs to role names
    const roleMap = new Map(allRoles.map((role) => [role.roleId, role.name]))

    // Get direct roles assigned to the user
    const directRoleIds = new Set(accountSubject.roles || [])

    // Find groups the user belongs to
    const userGroups = allGroups.filter((group) => group.accountSubjects?.includes(accountSubject.accountSubjectId))

    // Collect all unique role IDs (direct + from groups)
    const allRoleIds = new Set([...directRoleIds])
    const groupRoleIds: string[] = []

    userGroups.forEach((group) => {
      group.roles?.forEach((roleId) => {
        allRoleIds.add(roleId)
        groupRoleIds.push(roleId)
      })
    })

    // Build role display objects
    const roleDisplays: RoleDisplay[] = Array.from(allRoleIds)
      .map((roleId) => {
        const roleName = roleMap.get(roleId) || roleId
        const isFromGroup = groupRoleIds.includes(roleId) && !directRoleIds.has(roleId)

        // Get group names that provide this role
        const groupNames = userGroups
          .filter((group) => group.roles?.includes(roleId))
          .map((group) => group.name || group.accountSubjectGroupId)

        return {
          name: roleName,
          isFromGroup,
          isSsoRole: false,
          groupNames,
        }
      })
      .sort((a, b) => {
        // Sort: direct roles first, then group roles, alphabetically within each
        if (a.isFromGroup !== b.isFromGroup) {
          return a.isFromGroup ? 1 : -1
        }
        return a.name.localeCompare(b.name)
      })

    // Get group names
    const groupNames = userGroups
      .map((group) => group.name || group.accountSubjectGroupId)
      .sort((a, b) => a.localeCompare(b))

    // Compute SSO groups and roles using shared utility
    const ssoData = computeSsoRolesAndGroups(
      accountSubject.lastSsoGroups,
      accountSubject.lastSsoRoles,
      allGroups,
      allRoles
    )

    // Set the profile data
    this.setProfileData(principal, accountSubject, roleDisplays, groupNames, ssoData.ssoRoles, ssoData.ssoGroups)
  }

  private setProfileData(
    principal: AuthenticatedPrincipalDto,
    accountSubject: AccountSubjectDto,
    roleDisplays: RoleDisplay[],
    groups: string[],
    ssoRoles: { name: string; id: string }[],
    ssoGroups: { name: string; id: string }[]
  ) {
    // Set the form with the user's current name
    this.nameModel.setValue({ name: accountSubject.name || '' })

    this.userProfileData = {
      email: accountSubject.email || principal.subjectId || 'N/A',
      name: accountSubject.name || '',
      accountSubjectId: accountSubject.accountSubjectId || '',
      roles: roleDisplays,
      groups: groups,
      ssoRoles: ssoRoles,
      ssoGroups: ssoGroups,
    }
  }

  updateName() {
    if (!this.nameDirty || this.updatingName) {
      return
    }

    this.updateUserSubscription = this.apiService
      .updateAccountSubject(this.accountService.accountId, this.userProfileData.accountSubjectId, {
        name: this.nameModel.value.name,
      })
      .pipe(finalize(() => (this.updateUserSubscription = null)))
      .subscribe({
        next: () => {
          this.nameUpdated = true
          this.userProfileData.name = this.nameModel.value.name
        },
        error: () => {
          this.uiService.announceError(new BaseError('Update Failed', 'Unable to update your name. Please try again.'))
        },
      })
  }

  get nameDirty() {
    return this.userProfileData && this.userProfileData.name !== this.nameModel.value.name
  }

  get saveNameDisabled() {
    return !this.nameDirty || this.updatingName || !this.nameModel.valid
  }

  get updatingName() {
    return subscriptionActive(this.updateUserSubscription)
  }

  get updatedName() {
    return this.nameUpdated && !this.nameDirty
  }

  get loading() {
    return !this.userProfileData
  }
}
